import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Image, Type } from 'lucide-react';
import { DMButton, DMDiv, DMText } from '../../components/Primitives';

type Priority = 'High' | 'Medium' | 'Low';

interface OptimizationItem {
  title: string;
  description: string;
  priority: Priority;
  icon: typeof AlertCircle;
  iconLabel: string;
}

interface ApiOptimizationItem {
  issue?: string;
  action?: string;
  impact?: string;
  effort?: string;
  category?: string;
  priority?: string;
}

const optimizationItems: OptimizationItem[] = [
  {
    title: 'Fix Critical LCP Issues',
    description: 'Largest Contentful Paint exceeds 2.5s on mobile',
    priority: 'High',
    icon: AlertCircle,
    iconLabel: '!',
  },
  {
    title: 'Review Broken Links',
    description: '15 links leading to 404 errors detected',
    priority: 'Medium',
    icon: Type,
    iconLabel: 'T',
  },
  {
    title: 'Optimize Image Formats',
    description: 'Convert all images to WebP for faster loading times',
    priority: 'High',
    icon: AlertCircle,
    iconLabel: '!',
  },
  {
    title: 'Implement Lazy Loading',
    description:
      'Load images only when they enter the viewport to improve page speed',
    priority: 'High',
    icon: AlertCircle,
    iconLabel: '!',
  },
  {
    title: 'Implement Lazy Loading for Images',
    description: 'Load hero and background images only when visible on screen',
    priority: 'Low',
    icon: Image,
    iconLabel: '',
  },
  {
    title: 'Minify CSS and JavaScript',
    description:
      'Reduce file sizes by removing unnecessary characters and comments',
    priority: 'High',
    icon: AlertCircle,
    iconLabel: '!',
  },
  {
    title: 'Enhance Image Alt Texts',
    description: '35 images lack descriptive alternative text',
    priority: 'Medium',
    icon: Type,
    iconLabel: 'T',
  },
  {
    title: 'Leverage Browser Caching',
    description:
      'Set expiry dates for static resources to decrease load times on repeat visits',
    priority: 'High',
    icon: AlertCircle,
    iconLabel: '!',
  },
  {
    title: 'Compress Hero Image Assets',
    description: 'Convert 15 large PNGs to modern WebP format',
    priority: 'Low',
    icon: Image,
    iconLabel: '',
  },
];

const filters: Array<'All' | Priority> = ['All', 'High', 'Medium', 'Low'];

const priorityStyles: Record<
  Priority,
  { iconWrap: string; icon: string; badge: string; label: string }
> = {
  High: {
    iconWrap: 'bg-rose-50',
    icon: 'text-rose-600',
    badge: 'bg-rose-50 text-rose-700',
    label: 'HIGH PRIORITY',
  },
  Medium: {
    iconWrap: 'bg-yellow-50',
    icon: 'text-yellow-600',
    badge: 'bg-yellow-50 text-yellow-700',
    label: 'MEDIUM',
  },
  Low: {
    iconWrap: 'bg-emerald-50',
    icon: 'text-emerald-600',
    badge: 'bg-emerald-50 text-emerald-700',
    label: 'LOW PRIORITY',
  },
};

const OptimizationIcon = ({ item }: { item: OptimizationItem }) => {
  const Icon = item.icon;
  const styles = priorityStyles[item.priority];

  return (
    <DMText
      as="span"
      className={`flex h-[4.7vh] w-[4.7vh] shrink-0 items-center justify-center rounded-full ${styles.iconWrap}`}
      aria-hidden="true"
    >
      {item.iconLabel ? (
        <DMText
          as="span"
          className={`text-[2.45vh] font-extrabold leading-none ${styles.icon}`}
        >
          {item.iconLabel}
        </DMText>
      ) : (
        <Icon
          className={`h-[2.25vh] w-[2.25vh] ${styles.icon}`}
          strokeWidth={2.4}
        />
      )}
    </DMText>
  );
};

export default function OptimizationTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = React.useState<'All' | Priority>(
    'All'
  );
  const apiOptimizationItems = React.useMemo<ApiOptimizationItem[]>(() => {
    const stateItems = location.state?.optimizationPlan;

    if (Array.isArray(stateItems)) {
      return stateItems;
    }

    try {
      const storedItems = JSON.parse(
        sessionStorage.getItem('seoOptimizationPlan') || '[]'
      );
      return Array.isArray(storedItems) ? storedItems : [];
    } catch {
      return [];
    }
  }, [location.state]);

  const dynamicOptimizationItems = React.useMemo<OptimizationItem[]>(() => {
    if (!apiOptimizationItems.length) {
      return optimizationItems;
    }

    return apiOptimizationItems.map(item => {
      const priority =
        item.priority === 'LOW'
          ? 'Low'
          : item.priority === 'MEDIUM'
          ? 'Medium'
          : 'High';

      return {
        title: item.issue || item.category || 'SEO recommendation',
        description:
          item.action ||
          item.impact ||
          item.effort ||
          'Review this recommendation to improve SEO health.',
        priority,
        icon:
          priority === 'Low'
            ? Image
            : priority === 'Medium'
            ? Type
            : AlertCircle,
        iconLabel: priority === 'Low' ? '' : priority === 'Medium' ? 'T' : '!',
      };
    });
  }, [apiOptimizationItems]);

  const visibleItems =
    activeFilter === 'All'
      ? dynamicOptimizationItems
      : dynamicOptimizationItems.filter(item => item.priority === activeFilter);

  return (
    <DMDiv className="flex h-full min-h-0 flex-col overflow-hidden py-[0.55vh] text-gray-950">
      <DMDiv as="header" className="shrink-0">
        <DMButton
          type="button"
          onClick={() => navigate('/digital-marketing/seo')}
          className="inline-flex items-center gap-[0.5vh] text-[1.8vh] font-semibold leading-none text-indigo-600 transition hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <ArrowLeft className="h-[1.9vh] w-[1.9vh]" aria-hidden="true" />
          Back
        </DMButton>

        <DMDiv className="mt-[1.5vh] flex items-start justify-between gap-[3vh]">
          <DMDiv>
            <DMText
              as="h1"
              className="text-[3.85vh] font-extrabold leading-none text-gray-950"
            >
              Optimization Plan &mdash; {dynamicOptimizationItems.length}{' '}
              Changes Required
            </DMText>
            <DMText className="mt-[1.15vh] text-[2.05vh] font-medium leading-none text-gray-600">
              Prioritized checklist to boost your SEO health score
            </DMText>
          </DMDiv>

          <DMDiv
            className="flex h-[5.7vh] shrink-0 items-center gap-[0.6vh] rounded-full bg-gray-100 p-[0.7vh]"
            aria-label="Filter recommendations"
          >
            {filters.map(filter => {
              const isActive = activeFilter === filter;

              return (
                <DMButton
                  key={filter}
                  type="button"
                  onClick={() => setActiveFilter(filter)}
                  className={`h-[3.8vh] min-w-[7.2vh] rounded-full px-[2.2vh] text-[1.65vh] font-semibold leading-none transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-950 hover:bg-white'
                  }`}
                >
                  {filter}
                </DMButton>
              );
            })}
          </DMDiv>
        </DMDiv>
      </DMDiv>

      <DMDiv
        as="section"
        className="mt-[4.2vh] min-h-0 flex-1 overflow-auto"
        aria-label="Optimization recommendations"
      >
        <DMDiv as="ul">
          {visibleItems.map(item => {
            const styles = priorityStyles[item.priority];

            return (
              <DMDiv
                as="li"
                key={`${item.priority}-${item.title}`}
                className="flex h-[8.65vh] items-center justify-between gap-[3vh] border-b border-gray-100 px-[2.8vh] first:border-t first:border-gray-50"
              >
                <DMDiv className="flex min-w-0 items-center gap-[2vh]">
                  <OptimizationIcon item={item} />
                  <DMDiv className="min-w-0">
                    <DMText
                      as="h2"
                      className="truncate text-[1.95vh] font-extrabold leading-tight text-slate-900"
                    >
                      {item.title}
                    </DMText>
                    <DMText className="mt-[0.35vh] truncate text-[1.63vh] font-medium leading-tight text-gray-600">
                      {item.description}
                    </DMText>
                  </DMDiv>
                </DMDiv>

                <DMText
                  as="span"
                  className={`shrink-0 rounded-full px-[1.7vh] py-[0.75vh] text-[1.05vh] font-extrabold leading-none ${styles.badge}`}
                >
                  {styles.label}
                </DMText>
              </DMDiv>
            );
          })}
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
}
